import { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './style.module.css';
import TaskListRow from './TaskListRow';
import TaskListLoader from './TaskListLoader';
import { useNavigate } from 'react-router-dom';
import useApi from '../../services/useApi';
import { useRecoilState } from 'recoil';
import { selectedTaskState, newTaskConfig, taskListFilterState, namespaceState } from '../../recoil/atoms';
import useInterval from '../../hooks/useInterval';
import { taskConfigDefault } from '../../constants';
import Pagination from './Pagination';

export default function TaskList() {
  const [taskConfig, setTaskConfig] = useRecoilState(newTaskConfig);
  const [nsState] = useRecoilState(namespaceState);
  const [taskListFilter, setTaskListFilter] = useRecoilState(taskListFilterState);
  const [listData, setListData] = useState([]);
  const [listCount, setListCount] = useState();
  const [listLoading, setListLoading] = useState(true);
  const [, setSelectedTask] = useRecoilState(selectedTaskState);
  const userId = JSON.parse(localStorage.getItem('account')).id;

  const cancelToken = axios.CancelToken;
  let cancelApi = cancelToken.source();

  const handleSort = (key) => {
    let newAscending = taskListFilter.ascending;
    if (key === taskListFilter.sortBy) newAscending = !taskListFilter.ascending;

    setTaskListFilter((prev) => ({
      ...prev,
      sortBy: key,
      ascending: newAscending
    }));
  };

  useEffect(() => {
    setListLoading(true);
    cancelApi.cancel('Api Cancel');
    cancelApi = cancelToken.source();
    getTaskList();
  }, [taskListFilter.ascending, taskListFilter.pageNum, taskListFilter.isFilteredUserId, nsState]);

  const getTaskList = () => {
    useApi.get(
      'ngp/fl/task/list?namespaceId='+nsState.id+'&'+(taskListFilter.isFilteredUserId?('userId='+userId):"")
      +'&sortBy='+ taskListFilter.sortBy
      +'&order='+(taskListFilter.ascending?"ASC":"DESC")
      +'&size='+ taskListFilter.itemPerPage
      +'&page='+ (parseInt(taskListFilter.pageNum) - 1),
      { cancelToken: cancelApi.token }
    ).then((res)=>{
      setListData(res.data.items);
      setListCount(res.data.count);
      setListLoading(false);
    }).catch(function (thrown) {
      if (axios.isCancel(thrown)) {
        console.log('Request canceled', thrown.message);
      }
    });
  };

  useEffect(() => {
    cancelApi.cancel('Api Cancel');
    cancelApi = cancelToken.source();
    getTaskList();
    setTaskConfig(taskConfigDefault);
    setSelectedTask('');
  }, []);

  useInterval(() => {
    cancelApi.cancel('Api Cancel');
    cancelApi = cancelToken.source();
    getTaskList();
  }, 60000);

  const navigate = useNavigate();
  const handleNavigation = (newRoute, data) => {
    setSelectedTask(data);
    if (!document.startViewTransition) {
      return navigate(newRoute);
    } else {
      return document.startViewTransition(() => navigate(newRoute));
    }
  };

  const LoopTaskListLoader = () => {
    const newArr = [];
    for (let i = 0; i < 19; i++) newArr.push(<TaskListLoader key={i} />);
    return newArr;
  };

  const handleTaskListFilter = (key, value) => {
    setTaskListFilter((prev) => {
      const next = { ...prev };
      if (key === 'isFilteredUserId') {
        next.isFilteredUserId = !!value;
        next.pageNum = 1; 
      } else if (key === 'pageNum' || key === 'pageStep' || key === 'itemPerPage') {
        next[key] = Number(value);
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  return (
    <>
      <div className={styles.wrap}>
        <div className={styles.header}>
          <div className={styles['header-left']}>
            <span className={styles.title}>Task List</span>

            <div className={styles.switch}>
              <label className={styles['switch-label']}>
                <input
                  className={styles['switch-input']}
                  type="checkbox"
                  checked={taskListFilter.isFilteredUserId}
                  onChange={() =>
                    handleTaskListFilter('isFilteredUserId', !taskListFilter.isFilteredUserId)
                  }
                />
                <span className={styles['switch-slider']}></span>
              </label>
              <div className={styles['switch-text']}>
                <span>Total</span>
                <span>My</span>
              </div>
            </div>

            <button className={styles['refresh-btn']} onClick={getTaskList} />
          </div>
        </div>

        <div className={styles.body}>
          <div className={styles['grid-wrap']}>
            <div className={styles['grid-head']}>
              <div
                className={`${styles['table-head-item']} ${
                  taskListFilter.sortBy === 'taskId' &&
                  (taskListFilter.ascending ? styles['table-head-item-desc'] : styles['table-head-item-asc'])
                }`}
                onClick={() => handleSort('taskId')}
              >
                Task ID
              </div>
              <div
                className={`${styles['table-head-item']} ${
                  taskListFilter.sortBy === 'taskName' &&
                  (taskListFilter.ascending ? styles['table-head-item-desc'] : styles['table-head-item-asc'])
                }`}
                onClick={() => handleSort('taskName')}
              >
                Name
              </div>
              <div
                className={`${styles['table-head-item']} ${
                  taskListFilter.sortBy === 'createdTime' &&
                  (taskListFilter.ascending ? styles['table-head-item-desc'] : styles['table-head-item-asc'])
                }`}
                onClick={() => handleSort('createdTime')}
              >
                Date
              </div>
              <div className={styles['head-center']}>User</div>
              <div className={styles['head-center']}>Data</div>
              <div className={`${styles['head-center']} ${styles['table-head-row-devices']}`}>Devices</div>
              <div className={styles['head-center']}>Rounds</div>
              <div className={styles['head-center']}>Status</div>
            </div>

            <div className={styles['grid-body']} data-scroll-root="true">
              {listLoading ? (
                <div className={styles['grid-body-inner']}>{LoopTaskListLoader()}</div>
              ) : (
                <div className={styles['grid-body-inner']}>
                  {listData.map((row) => (
                    <TaskListRow
                      key={row.taskId}
                      data={row}
                      onClick={() => handleNavigation('/task-detail', row)}
                      getTaskList={getTaskList}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {listCount !== 0 && (
            <Pagination
              totalItems={listCount}
              pageSize={taskListFilter.itemPerPage}
              currentPage={Number(taskListFilter.pageNum) || 1}
              windowSize={4}
              onChange={(p) => handleTaskListFilter('pageNum', p)}
            />
          )}
        </div>
      </div>
    </>
  );
}